import React, { MouseEvent, useRef } from 'react'

interface PopupLinkProps {
  href: string
  className?: string
  children: React.ReactNode
  asBrowserTab?: boolean
}

function generateUniqId (prefix: string) {
  // append to prefix with alphanumerals and removes the decimal
  return `${prefix}_${Math.random().toString(36).substring(2, 11)}`
}

export const PopupLink: React.FC<PopupLinkProps> = ({
  href,
  className,
  children,
  asBrowserTab = false,
}: PopupLinkProps) => {
  const popupRef = useRef<Window | null>(null)
  const popupId = generateUniqId('popup')

  const openPopupWindow = (e: MouseEvent<HTMLDivElement>): void => {
    const target = e.target as HTMLElement
    const anchor = target.closest('a')

    if (anchor && anchor.href) {
      e.preventDefault()

      const url = anchor.href

      // Focus existing window if open and active
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.focus()

        // Update URL if the dynamically changed link dynamically points somewhere else
        popupRef.current.location.href = url
        return
      }

      if (asBrowserTab) {
        popupRef.current = window.open(
          url, '_blank'
        )
      } else {
        popupRef.current = window.open(
          url,
          popupId,
          'toolbar=0,scrollbars=1,location=0,status=0,menubar=1,resizable=1,width=700,height=550'
        )
      }
    }
  }

  // bubbling up the event to parent div to work if event handler is broken during when
  // the anchor tag gets dynamically changed for stitch navigation. This makes it so
  // doesn't matter how the anchor tag gets changed since the handler is on the parent
  return (
    <div onClickCapture={openPopupWindow} style={{ display: 'inline-block' }}>
      <a className={className} href={href}>
        {children}
      </a>
    </div>
  )
}
